/** Decode local images without blob: URLs, which many host CSPs intentionally block. */
export const MAX_LOCAL_IMAGE_BYTES = 50 * 1024 * 1024;

export function loadLocalImage(blob: Blob, timeoutMs = 5000): Promise<HTMLImageElement> {
  const mime = blob.type.toLowerCase();
  if (
    !['image/png', 'image/jpeg', 'image/webp'].includes(mime) ||
    !blob.size ||
    blob.size > MAX_LOCAL_IMAGE_BYTES ||
    !Number.isFinite(timeoutMs) ||
    timeoutMs <= 0
  )
    return Promise.reject(new Error('image size or deadline invalid'));
  if (typeof FileReader === 'undefined' || typeof Image === 'undefined')
    return Promise.reject(new Error('Image loading APIs unavailable'));
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const image = new Image();
    let settled = false;
    const finish = (success: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      reader.onload = reader.onerror = reader.onabort = null;
      image.onload = image.onerror = null;
      if (reader.readyState === 1) reader.abort();
      if (success) resolve(image);
      else {
        image.removeAttribute('src');
        reject(new Error('image decode failed'));
      }
    };
    const decoded = () => {
      const { naturalWidth: width, naturalHeight: height } = image;
      finish(
        Number.isSafeInteger(width) && width > 0 && Number.isSafeInteger(height) && height > 0,
      );
    };
    const timer = setTimeout(() => finish(false), timeoutMs);
    reader.onerror = reader.onabort = () => finish(false);
    image.onload = decoded;
    image.onerror = () => finish(false);
    reader.onload = () => {
      if (settled) return;
      if (typeof reader.result !== 'string' || !reader.result.startsWith(`data:${mime};base64,`)) {
        finish(false);
        return;
      }
      image.src = reader.result;
      // decode also handles cached images without relying on a later load event.
      if (typeof image.decode === 'function')
        void image.decode().then(decoded, () => finish(false));
    };
    try {
      reader.readAsDataURL(blob);
    } catch {
      finish(false);
    }
  });
}
